import { ref, computed, watch } from 'vue';
import { useOrders } from '@/features/orders/controller';
import { formatPrice } from '@/services/globalFunctions';
import { useUsers } from '@/features/users/controller';
import { useToast } from 'primevue/usetoast'
import { useBigBags } from '@/features/bigbags/controller';
import { useProducts } from '@/features/products/controller';
import type { BigBagInterface } from '@/features/bigbags/type';
import type { OrderProductInterface } from '@/features/orders/type';
import type { ProductInterface } from '@/features/products/type';
import { useStripeTerminal } from '@/features/stripeTerminal/controller'

const cartTotals = ref({
    total_ht: 0,
    total_tva: 0,
    total_ttc: 0,
    total_ecotax: 0,
    total_weight: 0,
    total_big_bags_ht: 0,
    total_big_bags_ttc: 0,
    delivery_cost: 0,
    is_delivery: false,
    distance: 0,
    distance_error: null as string | null,
    details: [] as any[],
});

// Supprimé : maintenant géré directement dans activeOrder
// const isDelivery = ref(false);
// const deliveryData = ref<DeliveryDataInterface>({...});

const debounceTimeout = ref<number | null>(null);

export function useCart() {
    const $userStore = useUsers();
    const $ordersStore = useOrders();
    const $productsStore = useProducts();
    const $bigBagsStore = useBigBags();
    const toast = useToast()
    const $stripeTerminal = useStripeTerminal();

    // utilise directement les produits du store pour éviter la désynchronisation
    const products = computed(() => $ordersStore.activeOrder?.products || []);

    watch(
        [() => $ordersStore.activeOrder?.products],
        () => {
            if (debounceTimeout.value) clearTimeout(debounceTimeout.value);
            debounceTimeout.value = window.setTimeout(async () => {
                await fetchCartTotals();
            }, 500);
        },
        { deep: true }
    );

    async function fetchCartTotals(deliveryParams?: { is_delivery: boolean, delivery_address: any }) {
        if (!$ordersStore.activeOrder?.products?.length) {
            cartTotals.value = { ...cartTotals.value, total_ttc: 0, total_ht: 0 };
            return;
        }

        const productsPayload = $ordersStore.activeOrder.products.map(p => ({
            product_id: p.product_id,
            quantity: p.quantity,
            big_bags: p.selectedBigBags ? p.selectedBigBags.map(bb => ({
                id: bb.bigBag.id,
                quantity: bb.quantity
            })) : []
        }));

        // Si le composant nous envoie des paramètres, on met à jour l'ordre, sinon on utilise l'état de l'ordre
        if (deliveryParams !== undefined) {
            $ordersStore.activeOrder.is_delivery = deliveryParams.is_delivery;
            if (deliveryParams.is_delivery && deliveryParams.delivery_address) {
                $ordersStore.activeOrder.delivery_data = {
                    street: deliveryParams.delivery_address.street || '',
                    zip_code: deliveryParams.delivery_address.zip_code || '',
                    city: deliveryParams.delivery_address.city || '',
                    contact_phone: $ordersStore.activeOrder.delivery_data?.contact_phone || '',
                    contact_email: $ordersStore.activeOrder.delivery_data?.contact_email || '',
                    instructions: $ordersStore.activeOrder.delivery_data?.instructions || '',
                    distance: $ordersStore.activeOrder.delivery_data?.distance || 0
                };
            } else if (!deliveryParams.is_delivery) {
                $ordersStore.activeOrder.delivery_data = null;
            }
        }

        const response = await $ordersStore.fetchCartTotals({
            items: productsPayload,
            is_delivery: $ordersStore.activeOrder.is_delivery || false,
            delivery_address: $ordersStore.activeOrder.is_delivery && $ordersStore.activeOrder.delivery_data ? {
                street: $ordersStore.activeOrder.delivery_data.street,
                zip_code: $ordersStore.activeOrder.delivery_data.zip_code,
                city: $ordersStore.activeOrder.delivery_data.city
            } : null
        });

        cartTotals.value = response.data;
        // On met à jour la distance dans l'ordre
        if (response.data.distance !== undefined && $ordersStore.activeOrder.delivery_data) {
            $ordersStore.activeOrder.delivery_data.distance = response.data.distance;
        }
        $ordersStore.activeOrder.total_cost = response.data.total_ttc;
    }

    function syncBigBagCartStock() {
        // Synchroniser les big bags sélectionnés au niveau des produits
        if (!$ordersStore.activeOrder?.products) return;

        $ordersStore.activeOrder.products.forEach(product => {
            if (product.wantsBigBag && product.selectedBigBags) {
                product.selectedBigBags = product.selectedBigBags.filter(selection => {
                    const bigBag = $bigBagsStore.bigBags.find((bg: BigBagInterface) => bg.id === selection.bigBag.id);

                    if (!bigBag) {
                        toast.add({
                            severity: 'warn',
                            summary: 'Big bag retiré du panier',
                            detail: `Le big bag « ${selection.bigBag.name} » a été retiré du produit « ${product.product?.name} » car il n'est plus en stock.`,
                            life: 5000,
                        });
                        return false;
                    }

                    // Met à jour les infos du big bag
                    selection.bigBag = bigBag;

                    // Ajuste la quantité si elle dépasse le stock
                    if (selection.quantity > bigBag.stock_unit) {
                        selection.quantity = bigBag.stock_unit;
                        toast.add({
                            severity: 'warn',
                            summary: 'Quantité ajustée',
                            detail: `La quantité du big bag « ${bigBag.name} » pour le produit « ${product.product?.name} » a été ajustée à ${bigBag.stock_unit} (stock disponible).`,
                            life: 5000,
                        });
                    }
                    return true;
                });

                // Si plus de big bags sélectionnés, désactiver wantsBigBag
                if (product.selectedBigBags.length === 0) {
                    product.wantsBigBag = false;
                }
            }
        });
    }

    // vide la sélection des big bags
    async function clearBigBags() {
        if ($ordersStore.activeOrder?.products) {
            // Nettoyer les big bags au niveau des produits
            $ordersStore.activeOrder.products.forEach(product => {
                product.wantsBigBag = false;
                product.selectedBigBags = undefined;
            });
        }

        // Nettoyer aussi les anciens big bags (pour compatibilité)
        if ($ordersStore.activeOrder) {
            $ordersStore.activeOrder.big_bags = [];
            $ordersStore.wantsBigBags = false;
        }
    }

    // total du poids des produits
    const totalWeight = computed(() => {
        if (!$ordersStore.activeOrder?.products) return formatPrice(0);
        const totalGrams = $ordersStore.activeOrder.products.reduce((sum, item) => sum + ((item.product?.density || 0) * (item.quantity || 1)), 0);
        return Math.round(totalGrams / 1000).toLocaleString('fr-FR'); // conversion grammes -> kilogrammes, sans décimales
    });

    // mise à jour de la quantité d'un produit
    async function updateQuantity(item: any) {
        if (item.quantity < 1) item.quantity = 1;
        if (item.maxStock && item.quantity > item.maxStock) item.quantity = item.maxStock;
    }

    // suppression d'un produit du panier
    async function removeFromCart(item: any) {
        if ($ordersStore.activeOrder && $ordersStore.activeOrder.products) {
            $ordersStore.activeOrder.products = $ordersStore.activeOrder.products.filter(p => p.product_id !== item.product_id);
        }
        if ($ordersStore.activeOrder && $ordersStore.activeOrder.products?.length === 0) {
            $ordersStore.activeOrder = null;
        }
    }

    // vide le panier
    async function clearCart() {
        clearBigBags()
        if ($ordersStore.activeOrder) {
            $ordersStore.activeOrder = null;
        }
    }

    // ajoute un produit au panier
    async function addToCart(product: any, quantity: number) {
        if (!product) return;
        if (!$ordersStore.activeOrder) {
            $ordersStore.activeOrder = {
                type: 'order',
                id: Date.now(),
                user_id: $userStore.loggedUser?.id || null,
                total_cost: 0,
                paid_at: null,
                retired_at: null,
                status: 'in_progress',
                construction_site: '',
                transaction_id: null,
                comment: null,
                created_at: new Date().toISOString(),
                products: [],
                big_bags: [],
                is_delivery: false,
                delivery_data: null
            };
        }
        if (!$ordersStore.activeOrder || !product || !$ordersStore.activeOrder.products) return;
        const existingIndex = $ordersStore.activeOrder.products.findIndex(p => p.product_id === product.id);

        addNewProduct(product, quantity, existingIndex);
    }

    // ajoute un nouveau produit ou met à jour un produit existant
    async function addNewProduct(product: ProductInterface, quantity: number, existingIndex: number) {
        if (!$ordersStore.activeOrder) return;
        if (!$ordersStore.activeOrder.products) {
            $ordersStore.activeOrder.products = [];
        }
        if (existingIndex >= 0) {
            $ordersStore.activeOrder.products[existingIndex].quantity = quantity;
        } else {
            $ordersStore.activeOrder.products.push({
                id: Date.now(),
                name: product.name,
                reference: product.reference,
                product_id: product.id,
                order_id: $ordersStore.activeOrder.id,
                quantity: quantity,
                product: product
            });
        }
    }

    // Synchronise le stock des produits du panier avec le backend
    async function syncCartStock() {
        if (!$ordersStore.activeOrder?.products) return;
        const ids = $ordersStore.activeOrder?.products.map(p => p.product_id);

        const res = await $productsStore.getProductsByIds(ids);
        const stocks: Record<number, number> = {};
        res.data.products.forEach((p: any) => {
            stocks[p.id] = p.stock_unit;
        });
        $ordersStore.activeOrder?.products.slice().forEach(item => {
            const updated = res.data.products.find((p: any) => p.id === item.product_id);
            if (updated) {
                item.product = updated;
                item.name = updated.name;
                item.reference = updated.reference;
            }

            if (!stocks[item.product_id] || stocks[item.product_id] < 1) {
                removeFromCart(item);
                toast.add({
                    severity: 'warn',
                    summary: 'Produit retiré du panier',
                    detail: `Le produit « ${item.name} » a été retiré du panier car il n'est plus en stock.`,
                    life: 5000,
                });
            } else if (item.quantity > stocks[item.product_id]) {
                item.quantity = stocks[item.product_id];
                toast.add({
                    severity: 'warn',
                    summary: 'Quantité ajustée',
                    detail: `La quantité du produit « ${item.name} » a été ajustée à ${stocks[item.product_id]} (stock disponible).`,
                    life: 5000,
                });
            }
        });
    }

    async function checkout(construction_site: string, company_id?: number, user_id?: number, comment?: string, email?: string, paymentMethod: 'sepa' | 'card' = 'sepa') {
        if (!$ordersStore.activeOrder) {
            toast.add({ severity: 'error', summary: 'Erreur', detail: 'Aucune commande active', life: 3000 });
            return;
        }

        try {
            if ($userStore.isStorekeeper) {
                const orderResult = await $ordersStore.validateStorekeeperOrder(
                    $ordersStore.activeOrder.total_cost, 'eur', $ordersStore.activeOrder,
                    construction_site, company_id, user_id, comment, email
                );
                if (orderResult.error) {
                    toast.add({ severity: 'error', summary: 'Erreur lors de la commande', detail: orderResult.data.error, life: 5000 });
                    syncBigBagCartStock(); syncCartStock();
                    return;
                }
            } else if ($userStore.loggedUser?.type === 'professional' && $userStore.loggedUser?.company?.sepa_payment_method_id && paymentMethod === 'sepa') {
                // Paiement SEPA pour les professionnels seulement si SEPA est choisi
                const orderResult = await $ordersStore.validateOrder(
                    $ordersStore.activeOrder.total_cost, 'eur', $ordersStore.activeOrder,
                    construction_site
                );
                if (orderResult.error) {
                    toast.add({ severity: 'error', summary: 'Erreur lors de la commande', detail: orderResult.data.error, life: 5000 });
                    syncBigBagCartStock(); syncCartStock();
                    return;
                }
            } else {
                // Pour le terminal Stripe (carte) - pour clients anonymes, non-pro, ou pro qui choisit carte
                const paymentResult = await $stripeTerminal.createPaymentIntent(
                    $ordersStore.activeOrder.total_cost, 'eur', $ordersStore.activeOrder,
                    construction_site, email
                );

                if (paymentResult.error) {
                    toast.add({ severity: 'error', summary: 'Erreur de paiement', detail: paymentResult.data.error, life: 5000 });
                    syncBigBagCartStock(); syncCartStock();
                    return;
                }

                toast.add({ severity: 'info', summary: 'Paiement en cours', detail: 'Veuillez présenter votre carte au terminal...', life: 0 });

                let paymentProcessing = await $stripeTerminal.processPayment(paymentResult.data.payment_intent_id);
                if (paymentProcessing.error) {
                    toast.add({ severity: 'error', summary: 'Erreur de traitement', detail: paymentProcessing.data.error, life: 5000 });
                    return;
                }

                const confirmedPayment = await $stripeTerminal.waitForPaymentConfirmation(paymentResult.data.payment_intent_id);
            }

            toast.removeAllGroups();
            toast.add({ severity: 'success', summary: 'Paiement réussi', detail: 'Votre commande a été validée avec succès !', life: 5000 });
            $ordersStore.activeOrder = null;
            return true;

        } catch (error: any) {
            // Gestion des erreurs
            toast.removeAllGroups();
            if (error.message === 'Payment timeout') {
                toast.add({ severity: 'warn', summary: 'Délai dépassé', detail: 'Le paiement a pris trop de temps. Veuillez réessayer.', life: 5000 });
            } else if (error.message.includes('canceled')) {
                toast.add({ severity: 'warn', summary: 'Paiement annulé', detail: 'Le paiement a été annulé.', life: 5000 });
            } else {
                toast.add({ severity: 'error', summary: 'Erreur de paiement', detail: 'Une erreur est survenue lors du paiement.', life: 5000 });
            }
            console.error('Erreur lors du checkout:', error);
            throw error;
        }
    }


    return {
        products,
        totalWeight,
        updateQuantity,
        removeFromCart,
        clearCart,
        addToCart,
        checkout,
        syncCartStock,
        // Big bags par produit
        syncBigBagCartStock,
        cartTotals,
        fetchCartTotals,
    };
}
